import React, { useState, useEffect } from 'react';
import { Row, Col, Typography, Card, Table, Tag, Form, Button, Space, Input } from 'antd';
import MainLayout from '../../layouts/MainLayout';
import { financialApi } from '../../utils/financialApi';
import { extractApiUserMessage } from '../../utils/api';
import { formatPriceToman } from '../../utils/formatPriceToman';
import dayjs from 'dayjs';
import jalaliday from 'jalali-dayjs';
import PersianDatePicker from '../../components/PersianDatePicker';
import { jalaliValueToApi } from '../../utils/jalaliApiDate';

dayjs.extend(jalaliday);
const { Title, Text } = Typography;

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form] = Form.useForm();

  const fetchTransactions = async (params = {}) => {
    try {
      setLoading(true);
      const res = await financialApi.getTransactions(params);
      const data = res?.data || res;
      const tr = Array.isArray(data) ? data : data?.transactions; setTransactions(Array.isArray(tr) ? tr : []);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const handleFilter = (values) => {
    const params = {};
    if (values.type) params.type = values.type;
    if (values.bank_account_id) params.bank_account_id = Number(values.bank_account_id);
    
    const startStr = jalaliValueToApi(values.start_date);
    if (startStr) params.start_date = startStr;
    
    const endStr = jalaliValueToApi(values.end_date);
    if (endStr) params.end_date = endStr;
    
    fetchTransactions(params);
  };

  const resetFilter = () => {
    form.resetFields();
    fetchTransactions();
  };

  const columns = [
    {
      title: 'شماره مرجع',
      dataIndex: 'id',
      key: 'id',
      render: (id) => <Text className="text-gray-500 font-semibold">TRX-{id}</Text>,
    },
    {
      title: 'نوع',
      dataIndex: 'transaction_type',
      key: 'type',
      render: (type) => {
        const types = {
          receipt: { color: 'success', text: 'دریافت' },
          payment: { color: 'error', text: 'پرداخت' },
          refund: { color: 'warning', text: 'عودت' },
          adjustment: { color: 'default', text: 'تعدیل' },
          expense: { color: 'processing', text: 'هزینه' },
          transfer: { color: 'purple', text: 'انتقال' },
        };
        const config = types[type] || { color: 'default', text: type };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: 'حساب بانکی',
      dataIndex: 'bank_account_id',
      key: 'bank_account_id',
    },
    {
      title: 'مبلغ (تومان)',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount, record) => {
        const isOut = ['payment', 'refund', 'expense'].includes(record.transaction_type);
        return (
          <span className={`font-semibold ${isOut ? 'text-red-500' : 'text-emerald-500'}`}>
            {isOut ? '-' : '+'}{formatPriceToman(amount || 0)}
          </span>
        );
      }
    },
    {
      title: 'تاریخ',
      dataIndex: 'created_at',
      key: 'date',
      render: (date) => (
        <span dir="ltr">
          {date ? dayjs(date).calendar('jalali').format('YYYY/MM/DD HH:mm') : '-'}
        </span>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="p-4 md:p-6 pb-24 max-w-[1600px] mx-auto animate-fade-in">
        <div className="mb-6">
          <Title level={3} className="m-0 text-gray-800 dark:text-gray-100 font-bold">
            مرکز تراکنش‌های مالی
          </Title>
          <Text className="text-gray-500 dark:text-gray-400">
            گزارش کامل تراکنش‌های ورودی و خروجی شرکت
          </Text>
        </div>

        <Card bordered={false} className="shadow-sm dark:bg-gray-800 rounded-2xl mb-6">
          <Form form={form} layout="inline" onFinish={handleFilter}>
            <Form.Item name="type" label="نوع تراکنش">
              <Input placeholder="مانند: receipt, payment..." />
            </Form.Item>
            <Form.Item name="bank_account_id" label="شناسه حساب بانکی">
              <Input type="number" />
            </Form.Item>
            <Form.Item name="start_date" label="از تاریخ">
              <PersianDatePicker />
            </Form.Item>
            <Form.Item name="end_date" label="تا تاریخ">
              <PersianDatePicker />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">اعمال فیلتر</Button>
                <Button onClick={resetFilter}>پاک کردن</Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>

        <Card 
          loading={loading}
          bordered={false} 
          className="shadow-sm dark:bg-gray-800 rounded-2xl h-full"
        >
          <Table 
            dataSource={transactions} 
            columns={columns}
            rowKey="id"
            pagination={true}
            size="middle"
            className="custom-table"
            locale={{ emptyText: 'تراکنشی یافت نشد' }}
          />
        </Card>
      </div>
    </MainLayout>
  );
};

export default Transactions;
